// FCM Push-Client für red.
//
// Aufgaben:
//   - Service Worker registrieren (/firebase-messaging-sw.js)
//   - Permission-Status abfragen
//   - Permission anfordern + FCM-Token holen
//   - Token an unseren Server schicken (api/push-register)
//   - Token wieder löschen (Opt-out)
//
// Privacy: Token wird NUR nach explizitem User-Klick auf "Push aktivieren" geholt.
// Vorher passiert nichts.

import { getMessaging, getToken, deleteToken, onMessage, isSupported } from 'firebase/messaging';
import { app } from '../firebase';
import { apiPost } from './apiClient';

// VAPID-Key defensiv säubern: Vercel/.env nehmen beim Einfügen gern Whitespace,
// Zeilenumbrüche oder umschließende Anführungszeichen mit. Ein VAPID-Key ist
// base64url (A–Z a–z 0–9 - _) und ~88 Zeichen lang; alles andere macht
// PushManager.subscribe → "applicationServerKey is not valid".
const VAPID_KEY = (process.env.REACT_APP_FCM_VAPID_KEY || '')
  .trim()
  .replace(/^["']|["']$/g, '')      // umschließende Quotes entfernen
  .replace(/\s+/g, '');             // jegliche Whitespace/Newlines entfernen

let _messaging = null;
let _supported = null;

export async function isPushSupported() {
  if (_supported !== null) return _supported;
  try {
    _supported = await isSupported();
  } catch {
    _supported = false;
  }
  return _supported;
}

async function getMessagingInstance() {
  if (_messaging) return _messaging;
  if (!(await isPushSupported())) return null;
  try {
    _messaging = getMessaging(app);
    return _messaging;
  } catch (err) {
    console.warn('[push] getMessaging fehlgeschlagen:', err?.message);
    return null;
  }
}

export function getPermissionState() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

export function isStandalonePWA() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
}

export function isIOSSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isiOS = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isiOS && isSafari;
}

/**
 * Registriert den Service Worker und holt einen FCM-Token.
 * Triggert den Browser-Permission-Dialog beim ersten Aufruf.
 * Sendet den Token sofort an unseren Server (api/push-register save).
 */
export async function enablePush() {
  if (!(await isPushSupported())) {
    throw new Error('Push wird in diesem Browser nicht unterstützt.');
  }
  if (!VAPID_KEY) {
    throw new Error('VAPID-Key fehlt in der Konfiguration.');
  }
  // Service Worker registrieren
  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

  // Permission anfordern (Browser-Dialog)
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    throw new Error('Benachrichtigungen wurden abgelehnt.');
  }

  const messaging = await getMessagingInstance();
  if (!messaging) throw new Error('Messaging nicht verfügbar.');

  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
  if (!token) throw new Error('Kein FCM-Token erhalten.');

  // Server-seitig speichern
  await apiPost('/api/push-register', {
    action: 'save',
    token,
    platform: navigator.platform || 'unknown',
    ua: (navigator.userAgent || '').slice(0, 200),
  });

  return { token };
}

/**
 * Selbstheiler: stellt sicher, dass — sofern die Browser-Erlaubnis bereits
 * erteilt ist — ein FCM-Token existiert UND server-seitig gespeichert wurde.
 *
 * Hintergrund: Wenn der User die Permission auf einem Build OHNE gültigen
 * VAPID-Key erteilt hat, wurde nie ein Token gespeichert (getToken schlug fehl),
 * aber Notification.permission ist trotzdem 'granted'. Die UI zeigt dann nur
 * noch "deaktivieren" — der "aktivieren"-Button (der den Token holt) erscheint
 * nicht mehr. Folge: der Cron findet kein Token → sent:0.
 *
 * Diese Funktion holt in genau dem Fall den Token nach und speichert ihn —
 * idempotent, ohne Permission-Dialog (Erlaubnis ist ja schon da). No-op, wenn
 * Push nicht unterstützt wird, Permission nicht 'granted' ist oder kein
 * VAPID-Key vorliegt.
 *
 * @param {boolean} [silent=true]  true → schluckt Fehler (Auto-Selbstheilung
 *   beim Screen-Laden, gibt false zurück). false → wirft die echte Ursache,
 *   damit der explizite "Aktivieren"-Klick eine konkrete Meldung zeigen kann.
 * @returns {Promise<boolean>} true, wenn ein Token gespeichert wurde
 */
export async function ensurePushRegistered(silent = true) {
  try {
    if (!(await isPushSupported())) throw new Error('Push wird in diesem Browser nicht unterstützt.');
    if (!VAPID_KEY) throw new Error('VAPID-Key fehlt im Build (REACT_APP_FCM_VAPID_KEY). Vercel-Redeploy nötig.');
    if (getPermissionState() !== 'granted') throw new Error('Keine Browser-Erlaubnis für Benachrichtigungen.');

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    // Auf Aktivierung des Service Workers warten — getToken scheitert sonst
    // gelegentlich direkt nach dem ersten Register (SW noch "installing").
    await navigator.serviceWorker.ready.catch(() => {});

    // Alte/kaputte Push-Subscription entfernen — eine mit einem früheren
    // (falschen) applicationServerKey angelegte Subscription lässt subscribe()
    // erneut mit "applicationServerKey is not valid" scheitern.
    try {
      const existing = await registration.pushManager.getSubscription();
      if (existing) await existing.unsubscribe();
    } catch { /* best effort */ }

    const messaging = await getMessagingInstance();
    if (!messaging) throw new Error('Firebase Messaging konnte nicht initialisiert werden.');

    // getToken mit Timeout — verhindert, dass der Button bei Netzproblemen
    // endlos im Lade-Zustand hängt.
    const token = await Promise.race([
      getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      }),
      new Promise((_, rej) => setTimeout(() => rej(new Error('Zeitüberschreitung — bitte erneut versuchen.')), 15000)),
    ]);
    if (!token) throw new Error('Kein FCM-Token erhalten.');

    await apiPost('/api/push-register', {
      action: 'save',
      token,
      platform: navigator.platform || 'unknown',
      ua: (navigator.userAgent || '').slice(0, 200),
    });
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[push] ensurePushRegistered:', err?.message);
    if (!silent) throw err;
    return false;
  }
}

/**
 * Deaktiviert Push: löscht Token auf Client + Server.
 */
export async function disablePush() {
  try {
    const messaging = await getMessagingInstance();
    if (messaging) {
      const currentToken = await getToken(messaging, { vapidKey: VAPID_KEY }).catch(() => null);
      if (currentToken) {
        await deleteToken(messaging).catch(() => null);
        await apiPost('/api/push-register', { action: 'delete', token: currentToken }).catch(() => null);
      }
    }
  } catch (err) {
    console.warn('[push] disablePush:', err?.message);
  }
}

/**
 * Foreground-Handler: Notifications, die ankommen während die App offen ist.
 * Standard-FCM-Verhalten zeigt sie nicht von selbst — wir zeigen sie manuell
 * oder lassen sie als In-App-Toast.
 */
export async function listenForeground(handler) {
  const messaging = await getMessagingInstance();
  if (!messaging) return () => {};
  return onMessage(messaging, handler);
}
